import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ControlContainer, UntypedFormBuilder, UntypedFormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { Region, SeriousIncidentType } from '@app/api-module/control-centre-api-client';
import { Subscription } from 'rxjs';
import { ClinicalFeedbackConstants } from 'src/app/clinical-feedback/clinical-feedback-constants';
import {
    DropdownFormElementModel,
    feedbackSeverityLevelList,
    FeedbackTypeOption,
    feedbackTypeOptionList,
    FormElementModel,
    incidentSeverityLevelList,
    seriousIncidentTypeAnz,
    seriousIncidentTypeUki
} from '../../../clinical-feedback.model';
import { NewClinicalFeedbackFormHelper } from '../../new-clinical-feedback.helper';

@Component({
    selector: 'app-feedback-form',
    templateUrl: './feedback-form.component.html',
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }]
})
export class FeedbackFormComponent implements OnInit, OnDestroy {
    @Input() region?: Region;

    formControlName = 'feedback';
    formGroup!: UntypedFormGroup;
    private readonly subscriptions = new Subscription();

    feedbackTypeOptionList = feedbackTypeOptionList;
    feedbackSeverityLevelList = feedbackSeverityLevelList;
    incidentSeverityLevelList = incidentSeverityLevelList;
    clinicalFeedbackConstants = ClinicalFeedbackConstants;

    seriousIncidentType = SeriousIncidentType;
    seriousIncidentTypeAnz = seriousIncidentTypeAnz;
    seriousIncidentTypeUki = seriousIncidentTypeUki;

    showSeriousIncidentType = false;
    showRegulatoryInvestigationType = false;
    showSeverityLevelDropdown = false;

    formElementModels = {
        feedbackDetails: new FormElementModel({
            id: 'feedbackDetails',
            fieldName: 'Feedback details',
            label: 'Please provide details on the feedback',
            placeholder: 'Add text (10,000 characters).'
        }),
        clinicalImpactDetails: new FormElementModel({
            id: 'clinicalImpactDetails',
            fieldName: 'Clinical impact details',
            label: 'Please provide details on Clinical Impact to the patient',
            placeholder: 'Add text (10,000 characters).'
        }),
        feedbackType: new DropdownFormElementModel({
            id: 'feedbackType',
            fieldName: 'Feedback type',
            label: 'Feedback type',
            options: feedbackTypeOptionList
        }),
        severityLevel: new DropdownFormElementModel({
            id: 'severityLevel',
            fieldName: 'Severity level',
            label: 'Severity level'
        }),
        seriousIncidentType: new DropdownFormElementModel({
            id: 'seriousIncidentType',
            fieldName: 'Serious Incident type',
            label: 'Type of Serious Incident'
        }),
        regulatoryInvestigationType: new FormElementModel({
            id: 'regulatoryInvestigationType',
            fieldName: 'Type of investigation',
            label: 'Type of investigation'
        })
    };

    constructor(private readonly fb: UntypedFormBuilder, public parent: FormGroupDirective) {}

    ngOnInit(): void {
        this.formElementModels['seriousIncidentType'].options = this.showAnz ? this.seriousIncidentTypeAnz : this.seriousIncidentTypeUki;

        this.formGroup = this.fb.group({
            feedbackType: ['', Validators.required],
            severityLevel: [{ value: '', disabled: true }, Validators.required],
            feedbackDetails: ['', Validators.maxLength(ClinicalFeedbackConstants.VALIDATOR_MAX_LENGTH_10000)],
            clinicalImpactDetails: ['', Validators.maxLength(ClinicalFeedbackConstants.VALIDATOR_MAX_LENGTH_10000)],
            seriousIncidentType: [{ value: '', disabled: true }, Validators.required],
            regulatoryInvestigationType: [{ value: null, disabled: true }]
        });

        this.parent.form.addControl(this.formControlName, this.formGroup);
        this.handleFormValueChanges();
    }

    get formControls() {
        return (this.parent.form.get('feedback') as UntypedFormGroup).controls;
    }

    get showAnz() {
        return this.region === Region.Anz;
    }

    get showUki() {
        return this.region === Region.Uki;
    }

    handleFormValueChanges() {
        this.subscriptions.add(
            this.formControls['feedbackType'].valueChanges.subscribe(value => {
                this.formControls['severityLevel'].reset();
                this.showSeriousIncidentType = false;
                this.showRegulatoryInvestigationType = false;

                if (value && this.showUki) {
                    this.showSeverityLevelDropdown = true;
                    this.formControls['severityLevel'].enable();
                } else {
                    this.showSeverityLevelDropdown = false;
                    this.formControls['severityLevel'].disable();
                }

                switch (value) {
                    case FeedbackTypeOption.ClinicalFeedback:
                        this.formElementModels['severityLevel'].options = feedbackSeverityLevelList;
                        this.formControls['seriousIncidentType'].disable();
                        break;

                    case FeedbackTypeOption.AdvisedSeriousIncident:
                        this.formElementModels['severityLevel'].options = incidentSeverityLevelList;
                        this.showSeriousIncidentType = true;
                        this.formControls['seriousIncidentType'].enable();
                        this.formControls['seriousIncidentType'].reset();
                        break;
                }
            })
        );

        this.subscriptions.add(
            NewClinicalFeedbackFormHelper.SetVisibilityOfControlsOnDropdownEvent(
                this.formControls['seriousIncidentType'].valueChanges,
                [this.formControls['regulatoryInvestigationType']],
                (visibility: boolean) => {
                    this.showRegulatoryInvestigationType = visibility;
                },
                SeriousIncidentType.RegulatoryInvestigations
            )
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}
